import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Statistic, Spin, message, Tag, List, Typography, Empty, Button, Collapse, Tooltip, Skeleton, Image, Badge } from 'antd';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { formatMoney } from '../../utils/money';
import {
  TeamOutlined, DollarOutlined, CalendarOutlined, UserAddOutlined,
  WarningOutlined, FileTextOutlined, UserOutlined, UserSwitchOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { getDashboardReport, getMonthlyTrend } from '../../api/reports';
import { getDailyNotes } from '../../api/dailyNotes';
import { getDailySchedule, updateSlotTeachers } from '../../api/schedule';
import { getAnnouncements } from '../../api/announcements';
import api from '../../api/axios';
import UserSearchSelect from '../common/UserSearchSelect';
import MissingProjectsPanel from './MissingProjectsPanel';
import MissingAttendancesPanel from './MissingAttendancesPanel';
import AgendaPendingPanel from './AgendaPendingPanel';
import WorkTaskPendingPanel from './WorkTaskPendingPanel';
import RoutinesTodayPanel from './RoutinesTodayPanel';
import ParentRequestsPendingPanel from './ParentRequestsPendingPanel';
import ParentProfileChangePendingPanel from './ParentProfileChangePendingPanel';
import LeaveRequestsPendingPanel from './LeaveRequestsPendingPanel';
import BackupStatusPanel from './BackupStatusPanel';
import CollapsibleCard from './CollapsibleCard';
import DraggableDashboard from './DraggableDashboard';

const { Text } = Typography;
const PIE_COLORS = ['#1677ff', '#faad14'];

const SectionSpin = () => (
  <div className="flex items-center justify-center py-6">
    <Spin />
  </div>
);

const AdminDashboard = () => {
  const [data, setData]               = useState(null);
  const [trend, setTrend]             = useState([]);
  const [notes, setNotes]             = useState([]);
  const [lessonNotes, setLessonNotes]         = useState([]);
  const [classroomOccupancy, setClassroomOcc] = useState([]);
  const [todaySlotCount, setTodaySlotCount]   = useState(0);
  const [announcements, setAnnouncements] = useState([]);
  const [pendingCerts, setPendingCerts] = useState([]);
  const [absences, setAbsences]         = useState([]);
  const [completedNotReenrolled, setCompletedNotReenrolled] = useState([]);
  const [assignState, setAssignState] = useState({});

  // Bölüm bazlı yükleme durumları
  const [loadingMain, setLoadingMain]         = useState(true);
  const [loadingTrend, setLoadingTrend]       = useState(true);
  const [loadingNotes, setLoadingNotes]       = useState(true);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [loadingCerts, setLoadingCerts]       = useState(true);
  const [loadingAbsences, setLoadingAbsences] = useState(true);
  const [loadingCompleted, setLoadingCompleted] = useState(true);

  const navigate = useNavigate();
  const today = dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    // Ana KPI + duyurular
    Promise.all([
      getDashboardReport(),
      getAnnouncements({ per_page: 3, is_active: 1 }),
    ]).then(([dashRes, annRes]) => {
      setData(dashRes.data);
      setAnnouncements(annRes.data?.data || []);
    }).catch(() => message.error('Dashboard verileri yüklenemedi.'))
      .finally(() => setLoadingMain(false));

    // Aylık trend
    getMonthlyTrend()
      .then(r => setTrend(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingTrend(false));

    // Günlük notlar
    getDailyNotes({ date: today })
      .then(r => setNotes(r.data?.data || r.data || []))
      .catch(() => {})
      .finally(() => setLoadingNotes(false));

    // Ders programı + derslik doluluk
    getDailySchedule({ date: today })
      .then(r => {
        const slots = r.data || [];
        setTodaySlotCount(slots.length);
        setLessonNotes(slots.filter(s => s.note));
        const map = {};
        slots.forEach(s => {
          const key = s.classroom_id;
          if (!map[key]) map[key] = { name: s.classroom_name, total: 0, capacity_sum: 0, session_count: 0 };
          map[key].total         += s.total || 0;
          map[key].capacity_sum  += s.capacity || 0;
          map[key].session_count += 1;
        });
        setClassroomOcc(Object.values(map).map(c => ({
          ...c,
          rate: c.capacity_sum > 0 ? Math.round((c.total / c.capacity_sum) * 100) : 0,
        })).sort((a, b) => b.rate - a.rate));
      })
      .catch(() => {})
      .finally(() => setLoadingSchedule(false));

    // Bekleyen sertifikalar
    api.get('/reports/pending-certificates')
      .then(r => setPendingCerts(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingCerts(false));

    // Bugünkü devamsızlar
    api.get('/reports/today-absences')
      .then(r => setAbsences(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingAbsences(false));

    // Sertifika tamamladı ama yeni kayıt yok
    api.get('/reports/completed-not-reenrolled')
      .then(r => setCompletedNotReenrolled(r.data || []))
      .catch(() => {})
      .finally(() => setLoadingCompleted(false));
  }, []);

  const slotKey = (s) => `${s.date}_${s.classroom_id}_${s.start_time}`;

  const toggleAssign = (key) =>
    setAssignState(prev => ({ ...prev, [key]: { open: !prev[key]?.open, selected: [], saving: false } }));

  const handleAssign = async (session) => {
    const key = slotKey(session);
    const st = assignState[key];
    if (!st?.selected?.length) return message.warning('Öğretmen seçin.');
    setAssignState(prev => ({ ...prev, [key]: { ...prev[key], saving: true } }));
    try {
      await updateSlotTeachers({
        date:         session.date,
        classroom_id: session.classroom_id,
        start_time:   session.start_time,
        teacher_ids:  st.selected,
      });
      message.success('Öğretmen atandı.');
      setData(prev => ({
        ...prev,
        unassigned_sessions: prev.unassigned_sessions.filter(s => slotKey(s) !== key),
      }));
    } catch {
      message.error('Atama yapılamadı.');
      setAssignState(prev => ({ ...prev, [key]: { ...prev[key], saving: false } }));
    }
  };


  const trendFormatted = trend.map(t => ({
    ...t,
    monthLabel: dayjs(t.month + '-01').format('MMM YY'),
    revenue: Number(t.revenue),
  }));

  const studentPie = [
    { name: 'Aktif', value: data?.students?.active   ?? 0 },
    { name: 'Aday',  value: data?.students?.candidate ?? 0 },
  ];

  const attendancePie = [
    { name: 'Geldi',   value: data?.today?.present ?? 0 },
    { name: 'Gelmedi', value: data?.today?.absent  ?? 0 },
  ];

  const widgets = [];

  widgets.push({ id: 'agenda', content: <AgendaPendingPanel /> });
  widgets.push({ id: 'work-tasks', content: <WorkTaskPendingPanel /> });
  widgets.push({ id: 'routines-today', content: <RoutinesTodayPanel /> });
  widgets.push({ id: 'parent-requests', content: <ParentRequestsPendingPanel /> });
  // Bilgi guncelleme istekleri AYRI panel: yalnizca yonetici gorur, ogretmen
  // panosunda hic cizilmez (bilesen kendi icinde de kontrol ediyor).
  widgets.push({ id: 'parent-profile-change', content: <ParentProfileChangePendingPanel /> });
  widgets.push({ id: 'leave-requests', content: <LeaveRequestsPendingPanel /> });
  widgets.push({ id: 'backup-status', content: <BackupStatusPanel /> });

  widgets.push({
    id: 'kpi',
    content: (
      <Row gutter={[16, 16]}>
        {loadingMain ? (
          [1,2,3,4,5,6].map(i => (
            <Col key={i} xs={12} sm={8} lg={4}>
              <Card size="small"><Skeleton active paragraph={{ rows: 1 }} title={false} /></Card>
            </Col>
          ))
        ) : [
          { title: 'Bugünkü Dersler', value: todaySlotCount,          icon: <CalendarOutlined />, color: '#1677ff', path: '/calendar' },
          { title: 'Bugün Gelen',       value: data?.today?.present,            icon: <TeamOutlined />,     color: '#52c41a' },
          { title: 'Aktif Öğrenciler',  value: data?.students?.active,          icon: <TeamOutlined />,     color: '#1677ff', path: '/students' },
          { title: 'Bekleyen Adaylar',  value: data?.students?.candidate,       icon: <UserAddOutlined />,  color: '#faad14', path: '/students?status=candidate' },
          // Para kartlarinda isMoney: AntD'nin varsayilan bicimlendiricisi
          // Amerikan ayracli ve ondalik sayisini gelen degerden aliyor -
          // biri "28,400.00 ₺" digeri "836,125 ₺" cikiyordu.
          { title: 'Bu Ay Tahsilat',    value: data?.finance?.monthly_payments, icon: <DollarOutlined />,   color: '#52c41a', isMoney: true, path: '/payments' },
          { title: 'Toplam Alacak',     value: data?.finance?.total_debt,       icon: <DollarOutlined />,   color: '#ff4d4f', isMoney: true },
        ].map((kpi, i) => (
          <Col key={i} xs={12} sm={8} lg={4}>
            <Card
              size="small"
              className={kpi.path ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}
              onClick={kpi.path ? () => navigate(kpi.path) : undefined}
            >
              <Statistic
                title={kpi.title}
                value={kpi.value ?? 0}
                prefix={kpi.icon}
                suffix={kpi.suffix}
                formatter={kpi.isMoney ? (v) => formatMoney(v) : undefined}
                valueStyle={{ color: kpi.color, fontSize: 20 }}
              />
            </Card>
          </Col>
        ))}
      </Row>
    ),
  });

  if (loadingAbsences || absences.length > 0) {
    widgets.push({
      id: 'today-absences',
      content: loadingAbsences ? (
        <Card size="small"><Skeleton active paragraph={{ rows: 1 }} /></Card>
      ) : (
        <CollapsibleCard
          title={<span className="text-red-500"><WarningOutlined className="mr-2" />Bugünün Devamsızları ({absences.length})</span>}
          style={{ borderColor: '#ff4d4f' }}
        >
          {absences.map(a => (
            <div key={a.attendance_id} className="flex justify-between items-center py-2 border-b last:border-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">{a.student_name}</span>
                <Tag style={{ margin: 0 }}>{a.classroom_name}</Tag>
                <Text type="secondary" className="text-xs">{a.start_time?.slice(0, 5)}</Text>
              </div>
            </div>
          ))}
        </CollapsibleCard>
      ),
    });
  }

  widgets.push({
    id: 'missing-attendances',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <MissingAttendancesPanel />
        </Col>
      </Row>
    ),
  });

  // KOŞULSUZ: sayı 0 olsa da kart çiziliyor, başlıkta gri rozetle.
  //
  // Eskiden `length > 0` koşuluyla gizleniyordu ve yönetici kartın VARLIĞINI
  // bilmiyordu — canlıda uyarı hiç çıkmadığı için (bkz. ReportController'daki
  // "derslikte de öğretmen yok" koşulu) kimse böyle bir denetimin yapıldığını
  // fark etmedi. Görünmeyen bir uyarı ile olmayan bir uyarı aynı şeydir.
  //
  // Panodaki diğer bekleyen-iş kartları (Veli Talepleri, İzin Talepleri…)
  // zaten boşken de başlık + gri rozet gösteriyor; bu kart tek istisnaydı.
  const ogretmensizDersler = data?.unassigned_sessions || [];
  widgets.push({
      id: 'unassigned-sessions',
      content: loadingMain ? (
        <Card size="small"><Skeleton active paragraph={{ rows: 2 }} /></Card>
      ) : (
        <CollapsibleCard
          title={
            <span className={ogretmensizDersler.length > 0 ? 'text-orange-500' : undefined}>
              <WarningOutlined className="mr-2" style={{ color: ogretmensizDersler.length > 0 ? undefined : '#bfbfbf' }} />
              Öğretmensiz Sertifika Programı Dersleri — Önümüzdeki 7 Gün
              <Badge
                count={ogretmensizDersler.length}
                showZero
                style={{ backgroundColor: ogretmensizDersler.length > 0 ? '#faad14' : '#d9d9d9', marginLeft: 8 }}
              />
            </span>
          }
          style={ogretmensizDersler.length > 0 ? { borderColor: '#fa8c16' } : undefined}
        >
          {ogretmensizDersler.length === 0 ? (
            <Empty
              description="Önümüzdeki 7 günde öğretmeni atanmamış ders yok."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : ogretmensizDersler.map(s => {
            const key = slotKey(s);
            const st = assignState[key];
            return (
              <div key={key} className="py-2 border-b last:border-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{s.classroom}</span>
                    {s.branch_name && <Tag color="geekblue">{s.branch_name}</Tag>}
                    <Text type="secondary" className="text-xs">
                      {dayjs(s.date).format('DD.MM.YYYY')} · {s.start_time}
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="small"
                      type={st?.open ? 'default' : 'primary'}
                      icon={<UserOutlined />}
                      onClick={() => toggleAssign(key)}
                    >
                      {st?.open ? 'İptal' : 'Öğretmen Ata'}
                    </Button>
                  </div>
                </div>
                {st?.open && (
                  <div className="flex items-center gap-2 mt-2">
                    <UserSearchSelect
                      mode="multiple"
                      size="small"
                      style={{ flex: 1 }}
                      placeholder="Öğretmen ara..."
                      value={st.selected}
                      onChange={val => setAssignState(prev => ({ ...prev, [key]: { ...prev[key], selected: val } }))}
                      extraParams={{ role: ['teacher', 'branch_manager', 'super_admin'] }}
                      renderLabel={(t) => t.branch?.name ? `${t.name} ${t.surname} (${t.branch.name})` : `${t.name} ${t.surname}`}
                    />
                    <Button size="small" type="primary" loading={st.saving} onClick={() => handleAssign(s)}>
                      Kaydet
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </CollapsibleCard>
      ),
  });

  widgets.push({
    id: 'missing-projects',
    content: (
      <MissingProjectsPanel
        todaySessions={data?.missing_projects_today || []}
        loading={loadingMain}
      />
    ),
  });

  widgets.push({
    id: 'charts-today',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6}>
          <CollapsibleCard title="Bugün Devam">
            {loadingMain ? <SectionSpin /> : (attendancePie[0].value === 0 && attendancePie[1].value === 0) ? (
              <Empty description="Henüz yoklama alınmadı" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={attendancePie}
                    cx="50%" cy="45%"
                    innerRadius={48} outerRadius={72}
                    dataKey="value"
                    paddingAngle={3}
                  >
                    <Cell fill="#52c41a" />
                    <Cell fill="#ff4d4f" />
                  </Pie>
                  <ChartTooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CollapsibleCard>
        </Col>

        <Col xs={24} lg={6}>
          <CollapsibleCard title="Öğrenci Durumu">
            {loadingMain ? <SectionSpin /> : (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={studentPie}
                    cx="50%" cy="45%"
                    innerRadius={48} outerRadius={72}
                    dataKey="value"
                    paddingAngle={3}
                  >
                    {studentPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                  </Pie>
                  <ChartTooltip formatter={(v, name) => [v, name]} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CollapsibleCard>
        </Col>

        <Col xs={24} lg={12}>
          <CollapsibleCard title="Bugün Derslik Doluluk" bodyStyle={{ maxHeight: 200, overflowY: 'auto' }}>
            {loadingSchedule ? <SectionSpin /> : classroomOccupancy.length === 0
              ? <Empty description="Bugün sertifika programı dersi yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              : classroomOccupancy.map((c, i) => (
                <div key={i} className="mb-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="truncate font-medium">{c.name}</span>
                    <span className="shrink-0 ml-1 text-gray-500">{c.total}/{c.capacity_sum} · %{c.rate}</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="h-2 rounded-full" style={{
                      width: `${Math.min(c.rate, 100)}%`,
                      background: c.rate >= 90 ? '#ff4d4f' : c.rate >= 70 ? '#faad14' : '#1677ff',
                    }} />
                  </div>
                </div>
              ))
            }
          </CollapsibleCard>
        </Col>
      </Row>
    ),
  });

  widgets.push({
    id: 'notes',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <CollapsibleCard
            title={<span><FileTextOutlined className="mr-2 text-orange-400" />Günlük Notlar</span>}
            extra={<a className="text-xs" onClick={() => navigate('/daily-notes')}>Tümü →</a>}
          >
            {loadingNotes ? <SectionSpin /> : notes.length === 0 ? (
              <Empty description="Bugün not yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List size="small" dataSource={notes} renderItem={n => (
                <List.Item>
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      {n.branch && <Tag color="geekblue" style={{ margin: 0 }}>{n.branch.name}</Tag>}
                      <Text type="secondary" className="text-xs">
                        {n.user?.name} {n.user?.surname} · {dayjs(n.created_at).format('HH:mm')}
                      </Text>
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{n.content}</div>
                  </div>
                </List.Item>
              )} />
            )}
          </CollapsibleCard>
        </Col>

        <Col xs={24} lg={12}>
          <CollapsibleCard
            title={<span><FileTextOutlined className="mr-2 text-blue-500" />Bugünün Ders Notları</span>}
            extra={<Text type="secondary" className="text-xs">{today}</Text>}
          >
            {loadingSchedule ? <SectionSpin /> : lessonNotes.length === 0 ? (
              <Empty description="Bugün sertifika programı notu eklenmemiş" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List size="small" dataSource={lessonNotes} renderItem={s => (
                <List.Item>
                  <div className="w-full">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <Tag style={{ margin: 0, background: s.color ? `${s.color}22` : '#e6f4ff', color: s.color || '#1677ff', borderColor: s.color || '#91caff' }}>
                        {s.classroom_name}
                      </Tag>
                      <Text type="secondary" className="text-xs">{s.start_time?.slice(0, 5)}</Text>
                      {s.teachers?.length > 0 && (
                        <Text type="secondary" className="text-xs">· {s.teachers.map(t => t.name).join(', ')}</Text>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{s.note}</div>
                  </div>
                </List.Item>
              )} />
            )}
          </CollapsibleCard>
        </Col>
      </Row>
    ),
  });

  widgets.push({
    id: 'recent',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <CollapsibleCard
            title={<span><UserSwitchOutlined className="mr-2 text-green-500" />Son 7 Günde Yeni Kayıtlar ({data?.recent_students?.length ?? 0})</span>}
            extra={<a className="text-xs" onClick={() => navigate('/students')}>Tümü →</a>}
          >
            {loadingMain ? <SectionSpin /> : !data?.recent_students?.length ? (
              <Empty description="Bu hafta yeni kayıt yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              data.recent_students.map(s => (
                <div key={s.id} className="flex justify-between items-center py-1.5 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{s.name}</span>
                    {s.branch && <Tag color="geekblue" style={{ margin: 0 }}>{s.branch}</Tag>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Tag color={s.status === 'active' ? 'green' : 'orange'} style={{ margin: 0 }}>
                      {s.status === 'active' ? 'Aktif' : 'Aday'}
                    </Tag>
                    <Text type="secondary" className="text-xs">{dayjs(s.created_at).format('DD.MM')}</Text>
                  </div>
                </div>
              ))
            )}
          </CollapsibleCard>
        </Col>

        <Col xs={24} lg={12}>
          <CollapsibleCard
            title={<span><DollarOutlined className="mr-2 text-green-500" />Son Ödemeler</span>}
            extra={<a className="text-xs" onClick={() => navigate('/payments')}>Tümü →</a>}
          >
            {loadingMain ? <SectionSpin /> : !data?.recent_payments?.length ? (
              <Empty description="Ödeme yok" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              data.recent_payments.map(p => {
                const methodLabel = { cash: 'Nakit', transfer: 'Havale', credit_card: 'K.Kartı', other: 'Diğer' }[p.payment_method] || p.payment_method;
                return (
                  <div key={p.id} className="flex justify-between items-center py-1.5 border-b last:border-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{p.student_name}</span>
                      <Tag color="green" style={{ margin: 0 }}>{methodLabel}</Tag>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-green-600 text-sm">{formatMoney(p.amount)}</span>
                      <span className="text-xs text-gray-400">{dayjs(p.paid_at).format('DD.MM')}</span>
                    </div>
                  </div>
                );
              })
            )}
          </CollapsibleCard>
        </Col>
      </Row>
    ),
  });

  widgets.push({
    id: 'trends',
    content: (
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <CollapsibleCard title="Aylık Gelir Trendi (12 Ay)">
            {loadingTrend ? <SectionSpin /> : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={trendFormatted}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#1677ff" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#1677ff" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <ChartTooltip formatter={v => [formatMoney(v), 'Gelir']} />
                  <Area type="monotone" dataKey="revenue" stroke="#1677ff" strokeWidth={2} fill="url(#revGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CollapsibleCard>
        </Col>

        <Col xs={24} lg={8}>
          <CollapsibleCard title="Yeni Öğrenci Trendi (12 Ay)">
            {loadingTrend ? <SectionSpin /> : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trendFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ChartTooltip />
                  <Bar dataKey="new_students"    name="Aday Öğrenci" fill="#1677ff" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="new_enrollments" name="Yeni Öğrenci" fill="#91caff" radius={[3, 3, 0, 0]} />
                  <Legend />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CollapsibleCard>
        </Col>
      </Row>
    ),
  });

  if (!loadingCompleted && completedNotReenrolled.length > 0) {
    widgets.push({
      id: 'completed-not-reenrolled',
      content: (
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <CollapsibleCard
              title={
                <span>
                  Sertifika Tamamladı, Yeni Kayıt Yok
                  <Tag color="purple" className="ml-2">{completedNotReenrolled.length}</Tag>
                </span>
              }
              extra={<span className="text-gray-400 text-xs">Aktif öğrenciler — sertifikayı bitirdi ama yeni programa kaydedilmedi</span>}
            >
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {completedNotReenrolled.map((item, i) => (
                  <div key={i} className="flex justify-between items-center p-2 border-b last:border-0">
                    <div>
                      <span className="font-medium">{item.student_name}</span>
                      <span className="ml-2 text-gray-400 text-sm">— {item.course_name}</span>
                      <span className="ml-2 text-gray-400 text-xs">{item.branch_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {dayjs(item.completed_at).format('DD.MM.YYYY')}
                      </span>
                      <Button size="small" onClick={() => navigate(`/students/${item.student_id}`)}>
                        Öğrenciye Git
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleCard>
          </Col>
        </Row>
      ),
    });
  }

  if (!loadingCerts && pendingCerts.length > 0) {
    widgets.push({
      id: 'pending-certs',
      content: (
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <CollapsibleCard
              title={
                <span>
                  PDF Hazırlanıyor
                  <Tag color="orange" className="ml-2">{pendingCerts.length}</Tag>
                </span>
              }
              extra={<span className="text-gray-400 text-xs">Sertifika kaydı oluşturuldu, PDF arka planda hazırlanıyor</span>}
            >
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {pendingCerts.map((item, i) => (
                  <div key={i} className="flex justify-between items-center p-2 border-b last:border-0">
                    <div>
                      <span className="font-medium">{item.student_name}</span>
                      <span className="ml-2 text-gray-400 text-sm">— {item.course_name}</span>
                      <span className="ml-2 text-gray-400 text-xs">{item.cert_number}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Tag color="orange">Hazırlanıyor</Tag>
                      <Button size="small" onClick={() => navigate(`/students/${item.student_id}`)}>
                        Öğrenciye Git
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CollapsibleCard>
          </Col>
        </Row>
      ),
    });
  }

  if (!loadingMain && announcements.length > 0) {
    widgets.push({
      id: 'announcements',
      content: (
        <CollapsibleCard title="Son Duyurular">
          <Collapse ghost size="small" items={announcements.map(a => ({
            key: a.id,
            label: (
              <div className="flex justify-between items-center w-full pr-4">
                <span className="font-semibold text-sm">{a.title}</span>
                <Text type="secondary" className="text-xs shrink-0 ml-2">{dayjs(a.published_at).format('DD.MM.YYYY')}</Text>
              </div>
            ),
            children: (
              <div style={{ padding: '4px 8px' }}>
                {a.image_url && <Image src={a.image_url} alt="" style={{ maxHeight: 180, borderRadius: 8, marginBottom: 8, display: 'block', cursor: 'pointer' }} />}
                <div className="text-gray-600 text-sm ql-editor prose max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(a.body) }} />
              </div>
            ),
          }))} />
        </CollapsibleCard>
      ),
    });
  }

  return <DraggableDashboard storageKey="admin" widgets={widgets} />;
};

export default AdminDashboard;
